import fastdom from 'fastdom';

import changeButtonIcon from '../helpers/change-button-icon';
import getNewMode from '../helpers/get-new-mode';

import initTooltip from 'components/tooltip';
class ColumnHead {
  constructor(head, button, { columnName, initialMode, hasHPeriod, level }) {
    this.level = level;
    this.mode = initialMode;
    this.columnName = columnName;
    this.hasHPeriod = hasHPeriod;

    this.head = head;
    this.button = button;
    this.siblingButton = null;
  }

  applyTransformations(mode, newMode) {
    /* from 8-hour to 3-hour */
    if (mode === 'p' && newMode === 't') {
      /* if the column has 1-hour period and ColumnHead instance is level 1  */
      if (this.hasHPeriod && this.level === 1) {
        this.siblingButton = this.createSecondaryButton();
        this.siblingButton.siblingButton = this;

        changeButtonIcon('shrink', this.button);

        this.button.classList.add('is-half');
        this.button.classList.add('is-on-left');

        this.siblingButton.insertButtonAfter(this.button);

        return this.siblingButton;
      }

      changeButtonIcon('shrink', this.button);
      initTooltip();

      return null;
    }

    /* from 8-hour to 1-hour */
    if (mode === 'p' && newMode === 'h') {
      this.siblingButton = this.createSecondaryButton();
      this.siblingButton.mode = 'h';
      this.siblingButton.siblingButton = this;

      changeButtonIcon('shrink', this.button);

      this.button.classList.add('is-half');
      this.button.classList.add('is-on-left');

      this.siblingButton.insertButtonAfter(this.button);
      this.siblingButton.applyTransformations('t', 'h');
      return this.siblingButton;
    }

    /* from 3-hour to 8-hour */
    if (mode === 't' && newMode === 'p') {
      changeButtonIcon('expand', this.button);

      /* if the column has 1-hour period and ColumnHead instance is level 1  */
      if (this.hasHPeriod && this.level === 1) {
        this.button.classList.remove('is-half');
        this.button.classList.remove('is-on-left');

        this.siblingButton.removeButton();
        this.siblingButton = null;
      }

      initTooltip();

      return null;
    }

    /* from 3-hour to 1-hour */
    if (mode === 't' && newMode === 'h') {
      changeButtonIcon('shrink', this.button);

      /* ColumnHead instance is level 2, assumes it was created by another instance */
      if (this.level === 2) {
        this.button.classList.remove('is-half');
        this.button.classList.remove('is-on-right');

        this.siblingButton.removeButton();
      }

      return null;
    }

    /* from 1-hour to 3-hour */
    if (mode === 'h' && newMode === 't') {
      changeButtonIcon('expand', this.button);

      /* ColumnHead instance is level 2, assumes it was created by another instance */
      if (this.level === 2) {
        this.button.classList.add('is-half');
        this.button.classList.add('is-on-right');

        this.siblingButton.insertButtonBefore(this.button);

        return null;
      }
    }

    return null;
  }

  createSecondaryButton() {
    const secondary = this.button.cloneNode(true);
    secondary.classList.add('is-half');
    secondary.classList.add('is-on-right');
    secondary.classList.remove('is-on-left');

    const columnHead = new ColumnHead(this.head, secondary, {
      level: 2,
      initialMode: 't',
      hasHPeriod: true,
      columnName: this.columnName,
    });
    return columnHead;
  }

  insertButtonAfter(referenceNode) {
    const { parentNode, nextSibling } = referenceNode;
    parentNode.insertBefore(this.button, nextSibling);
  }

  insertButtonBefore(referenceNode) {
    const { parentNode } = referenceNode;
    parentNode.insertBefore(this.button, referenceNode);
  }

  removeButton() {
    this.button.remove();
  }

  setClickListener(changeMode) {
    const self = this;
    let timer = null;
    let attempts = 0;

    function click(event) {
      event.preventDefault();

      if (window.navigator && window.navigator.vibrate)
        window.navigator.vibrate(50);

      const newMode = getNewMode(
        self.mode,
        (self.level === 1 && self.hasHPeriod && !self.siblingButton) ||
          self.level === 2
      );

      timer = setTimeout(
        () => fastdom.mutate(() => self.button.classList.add('is-loading')),
        350
      );

      function run(err) {
        if (err) {
          if (
            err.name === 'TableNoDataError' ||
            err.name === 'TableIsRunningError'
          ) {
            if (attempts < 3) {
              setTimeout(() => {
                attempts += 1;
                changeMode(self.columnName, self.mode, newMode, run);
              }, 3000);
              return;
            }
          }

          console.error(err); /* eslint-disable-line no-console */
          clearInterval(timer);
          if (timer !== null)
            fastdom.mutate(() => {
              self.button.classList.remove('is-loading');
              timer = null;
            });

          attempts = 0;
          return;
        }

        clearInterval(timer);
        if (timer !== null)
          fastdom.mutate(() => {
            self.button.classList.remove('is-loading');
            timer = null;
          });

        const columnHead = self.applyTransformations(self.mode, newMode);

        if (columnHead) columnHead.setClickListener(changeMode);

        attempts = 0;
        self.mode = newMode;
      }

      changeMode(self.columnName, self.mode, newMode, run);
    }

    self.button.addEventListener('click', click);
  }

  static construct(head) {
    const button = head.querySelector('button');
    if (!button || Object.hasOwnProperty.call(button.dataset, 'isLocked'))
      return null;

    const { dayName, mode } = head.dataset;
    const hasHPeriod = Object.hasOwnProperty.call(head.dataset, 'hasHPeriod');

    return new ColumnHead(head, button, {
      level: 1,
      hasHPeriod,
      columnName: dayName,
      initialMode: mode,
    });
  }
}

export default ColumnHead;
